import React, { useState, useEffect } from "react";
import { Tabs, Text, Button, Avatar, Popover, Spacer, useTheme, Link as GeistLink, Divider } from "@geist-ui/core";
import { useHistory, useLocation } from "react-router-dom";
import { BrandLogo } from "../brand-logo";
import { LogOut, User, Settings, CreditCard, Shield, Briefcase, ChevronDown, Package, Info, Lock } from "@geist-ui/icons";
import { motion } from "framer-motion";

interface GeistNavigationProps {
  user: any;
  logout: any;
  referralCode?: string;
  dashboard?: any;
}

export default function GeistNavigation({ user, logout, referralCode, dashboard }: GeistNavigationProps) {
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();
  
  const [activeTab, setActiveTab] = useState<string>("");
  
  // Determine if user is an investor (has at least one delivered/eingebucht investment)
  const deliveredInvestments = dashboard?.investments?.filter((inv: any) => inv.state === 'DELIVERED') || [];
  const isInvestor = deliveredInvestments.length > 0;
  const userStatus = isInvestor ? 'INVESTOR' : 'MEMBER';

  useEffect(() => {
    // Set active tab based on current path
    const path = location.pathname;
    if (path.includes("/direct/dashboard")) setActiveTab("dashboard");
    else if (path.includes("/direct/profile")) setActiveTab("profile");
    else if (path.includes("/direct/bank-and-tax")) setActiveTab("bank");
    else if (path.includes("/direct/legal-person")) setActiveTab("legal");
    else if (path.includes("/direct/security")) setActiveTab("security");
  }, [location]);

  const handleTabChange = (val: string) => {
    setActiveTab(val);
    switch (val) {
      case "dashboard": history.push("/direct/dashboard"); break;
      case "profile": history.push("/direct/profile"); break;
      case "bank": history.push("/direct/bank-and-tax"); break;
      case "legal": history.push("/direct/legal-person"); break;
      case "security": history.push("/direct/security"); break;
    }
  };

  const [menuVisible, setMenuVisible] = useState(false);
  const handleMenuChange = (next: boolean) => setMenuVisible(next);

  /* Define Menu Item Component for consistency */
  const MenuItem = ({ icon: Icon, label, onClick, danger = false }: any) => (
    <div 
        onClick={() => {
            onClick();
            setMenuVisible(false);
        }}
        style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px', 
            padding: '10px 16px', 
            cursor: 'pointer',
            borderRadius: '10px',
            transition: 'background 0.15s ease',
            color: danger ? '#DC2626' : '#000',
            fontFamily: '"Nunito Sans", sans-serif'
        }}
        onMouseEnter={(e: any) => e.currentTarget.style.backgroundColor = '#F5F5F5'}
        onMouseLeave={(e: any) => e.currentTarget.style.backgroundColor = 'transparent'}
    >
        <Icon size={18} />
        <Text span style={{ fontSize: '14px', fontWeight: 600, fontFamily: '"Nunito Sans", sans-serif' }}>{label}</Text>
    </div>
  );

  /* Custom Avatar to fix rendering artifacts */
  const CustomAvatar = ({ src, initials, scale = 1 }: any) => (
    <div style={{
        width: `${28 * scale}px`,
        height: `${28 * scale}px`,
        borderRadius: '50%',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#fff',
        border: '1px solid #eaeaea',
        fontSize: `${11 * scale}px`,
        fontWeight: 600,
        color: '#666',
        flexShrink: 0,
        userSelect: 'none'
    }}>
        {src ? (
            <img 
                src={src} 
                style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                onError={(e: any) => e.target.style.display = 'none'} 
                alt=""
            />
        ) : (
            <span>{initials}</span>
        )}
    </div>
  );

  const UserMenu = () => (
    <div style={{ width: '240px', padding: '8px' }}>
        <div style={{ padding: '12px 16px', marginBottom: '8px' }}>
            <Text small b style={{ display: 'block', lineHeight: 1.3, fontSize: '14px', fontWeight: 800, fontFamily: '"Nunito Sans", sans-serif', color: '#000' }}>
                {user?.natural_person?.forename} {user?.natural_person?.surname}
            </Text>
            <Text small style={{ color: '#888', display: 'block', fontSize: '13px', marginTop: '4px', fontFamily: '"Nunito Sans", sans-serif' }}>{user?.email}</Text>
        </div>
        
        <div style={{ width: '100%', height: '1px', background: '#F0F0F0', margin: '8px 0' }} />
        
        <MenuItem 
            icon={User} 
            label="Profil" 
            onClick={() => { history.push("/direct/profile"); }} 
        />
        <MenuItem 
            icon={CreditCard} 
            label="Bank & Steuern" 
            onClick={() => { history.push("/direct/bank-and-tax"); }} 
        />
        {user?.legal_person && (
            <MenuItem 
                icon={Briefcase} 
                label="Juristische Person" 
                onClick={() => { history.push("/direct/legal-person"); }} 
            />
        )}
        <MenuItem 
            icon={Settings} 
            label="Sicherheit" 
            onClick={() => { history.push("/direct/security"); }} 
        />
        
        <div style={{ width: '100%', height: '1px', background: '#F0F0F0', margin: '8px 0' }} />
        
        <MenuItem 
            icon={LogOut} 
            label="Abmelden" 
            danger
            onClick={() => logout({ returnTo: window.location.origin + "/direct/dashboard" })} 
        />
    </div>
  );

  const userInitials = user?.natural_person?.forename 
    ? (user.natural_person.forename[0] + (user.natural_person.surname?.[0] || '')).toUpperCase()
    : user?.email?.[0]?.toUpperCase();

  const springConfig = { stiffness: 400, damping: 32, mass: 1 };

  return (
    <div className="geist-nav-sticky-wrapper" style={{ 
        position: 'sticky',
        top: 0,
        zIndex: 9999,
        isolation: 'isolate',
        backgroundColor: 'rgba(255, 255, 255, 0.85)', 
        backdropFilter: 'saturate(180%) blur(20px)',
        borderBottom: `1px solid rgba(0, 0, 0, 0.05)`,
        transition: 'background-color 0.4s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
        boxShadow: menuVisible ? '0 10px 40px rgba(0,0,0,0.06)' : 'none',
        transform: 'translateZ(0)',
        willChange: 'background-color, box-shadow'
    }}>
      <style>
        {`
          .user-menu-popover {
            z-index: 10001 !important;
          }
          @keyframes status-pulse {
            0% { transform: scale(1) translateZ(0); opacity: 0.6; }
            50% { transform: scale(1.6) translateZ(0); opacity: 0; }
            100% { transform: scale(1) translateZ(0); opacity: 0.6; }
          }
          @media (max-width: 768px) {
            .desktop-nav { display: none !important; }
            .header-content { padding: 0 16px !important; height: 56px !important; }
            .member-status { scale: 0.85; transform-origin: right center; }
            .brand-logo-container { scale: 0.9; transform-origin: left center; }
          }
        `}
      </style>
      <div className="header-content" style={{ maxWidth: "1000pt", margin: "0 auto", padding: "0 24px", height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', transition: 'all 0.3s ease' }}>
        
        <div className="brand-logo-container" style={{ display: "flex", alignItems: "center", gap: "48px", height: '100%' }}>
            <motion.div 
                whileHover={{ opacity: 0.7 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => history.push("/direct/dashboard")}
                style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', willChange: 'opacity, transform' }}
            >
                <BrandLogo style={{ height: '22px' }} />
            </motion.div>

            <nav className="desktop-nav" style={{ display: 'flex', gap: '4px', alignItems: 'center', height: '100%' }}>
                <NavLink active={activeTab === 'dashboard'} onClick={() => handleTabChange('dashboard')} spring={springConfig}>
                    Portfolio
                </NavLink>
            </nav>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "24px", height: '100%' }}>
            <Popover 
                content={<UserMenu />} 
                placement="bottomEnd" 
                portalClassName="user-menu-popover"
                visible={menuVisible}
                onVisibleChange={handleMenuChange}
            >
                <motion.div 
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.96 }}
                    transition={{ type: 'spring', ...springConfig }}
                    style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px', 
                        cursor: 'pointer', 
                        padding: '0 8px 0 4px', 
                        borderRadius: '100px', 
                        background: menuVisible ? 'rgba(0,0,0,0.04)' : 'transparent',
                        border: '1px solid rgba(0,0,0,0.05)',
                        willChange: 'transform, background-color',
                        height: '36px'
                    }}
                >
                    <CustomAvatar 
                        src={user?.picture} 
                        initials={userInitials} 
                        scale={1.1} 
                    />
                    <ChevronDown size={14} color="#000" style={{ transition: 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)', transform: menuVisible ? 'rotate(180deg)' : 'none' }} />
                </motion.div>
            </Popover>
        </div>
      </div>
    </div>
  );
}

const NavLink = ({ active, children, onClick, spring }: any) => {
    return (
        <motion.div 
            onClick={onClick}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            transition={{ type: 'spring', ...spring }}
            style={{ 
                cursor: 'pointer', 
                fontSize: '13px', 
                color: active ? '#000' : '#777',
                padding: '0 20px',
                height: '36px',
                borderRadius: '100px',
                background: active ? 'rgba(0,0,0,0.05)' : 'transparent',
                fontWeight: active ? 800 : 600,
                fontFamily: '"Nunito Sans", sans-serif',
                letterSpacing: '-0.01em',
                userSelect: 'none',
                display: 'grid',
                placeItems: 'center',
                willChange: 'transform, background-color, color',
                lineHeight: 1
            }}
        >
            <span style={{ display: 'block', height: '13px', marginTop: '1px' }}>{children}</span>
        </motion.div>
    )
}
